<template>
  <div class="flex">
    <!-- Table Section -->
    <div :class="['overflow-x-auto transition-all', { 'w-full': !showPanel, 'w-2/3': showPanel }]">
      <table class="min-w-full bg-white border border-gray-200 rounded-md shadow">
        <thead>
          <tr class="bg-gray-100">
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Institution Name</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Admin Name</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Onboarded By</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Payment Status</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Status</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="institution in institutions" :key="institution.institution_id" class="border-t">
            <td class="py-3 px-4">{{ institution.name }}</td>
            <td class="py-3 px-4">{{ institution.institution_admins[0]?.admin_name || 'N/A' }}</td>
            <td class="py-3 px-4">{{ institution.onboardedBy || 'Self-Onboarded' }}</td>
            <td class="py-3 px-4">
              <span :class="{
                'px-4 py-1 rounded-md text-text-lg': true,
                 'bg-red-200 text-red-800': institution.payment_status === false,
                'bg-green-200 text-green-800': institution.payment_status === true,
              }">
                {{ institution.payment_status === false ? 'Pending' : 'Paid' }}
              </span>
            </td>
            <td class="py-3 px-4">
              <span :class="{
                'px-4 py-1 rounded-md text-text-lg': true,
                'bg-yellow-200 text-yellow-800': institution.status.name === 'Pending',
                'bg-blue-200 text-blue-800': institution.status.name === 'Verified',
                'bg-green-200 text-green-800': institution.status.name === 'Activated',
                'bg-red-200 text-red-800': institution.status.name === 'Activation Rejected' || institution.status.name === 'Suspended' || institution.status.name === 'Verification Rejected'
              }">
                {{ institution.status.name }}
              </span>
            </td>
            <td class="py-3 px-4">
              <button @click="openPanel(institution)"
                class="bg-orange-400 text-white px-4 py-2 rounded hover:bg-orange-600">
                View Details
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="flex justify-center items-center my-8">
        <div class="flex space-x-2">
          <!-- Previous Button -->
          <button 
            @click="changePage(currentPage - 1)" 
            :disabled="currentPage === 1"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <!-- Page Numbers -->
          <button 
            v-for="page in totalPages" 
            :key="page"
            @click="changePage(page)"
            :class="{ 
              'bg-blue-900 text-white': currentPage === page, 
              'text-gray-600': currentPage !== page 
            }"
            class="px-3 py-1 rounded hover:bg-blue-100"
          >
            {{ page }}
          </button>

          <!-- Next Button -->
          <button 
            @click="changePage(currentPage + 1)" 
            :disabled="currentPage === totalPages"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Right Side Panel -->
    <div v-if="showPanel"
      class="w-1/3 bg-white px-2 border-l border-gray-200 transition-all flex flex-col max-h-[74vh]">
      <div class="flex justify-between items-center p-3 bg-gray-100 border-b border-gray-200">
        <span :class="{
          'px-4 py-1 rounded-md text-text-lg': true,
          'bg-yellow-200 text-yellow-800': selectedInstitution?.status.name === 'Pending',
          'bg-blue-200 text-blue-800': selectedInstitution?.status.name === 'Verified',
          'bg-green-200 text-green-800': selectedInstitution?.status.name === 'Activated',
          'bg-red-200 text-red-800': selectedInstitution?.status.name === 'Activation Rejected' || selectedInstitution?.status.name === 'Suspended' || selectedInstitution?.status.name === 'Verification Rejected'
        }">
          {{ selectedInstitution?.status.name || 'Pending' }}
        </span>
        <div class="flex items-center">
          <button @click="closePanel" class="text-gray-500 hover:text-gray-700 p-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <div class="overflow-y-auto flex-grow">
        <div class="p-4">
          <h2 class="text-xl font-bold mb-4">Institution Information</h2>
          <div class="bg-white rounded-md border border-gray-200 p-5 mb-6">
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm">Institution Name:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedInstitution?.name || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Email Address:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedInstitution?.email || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Website URL:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">
                <a
                    :href="normalizedUrl(selectedInstitution?.website_url)"
                    class="text-blue-500 hover:underline"
                    :class="{ 'pointer-events-none text-gray-500': !selectedInstitution?.website_url }"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                  {{ selectedInstitution?.website_url ? formatUrl(selectedInstitution.website_url) : 'N/A' }}
                </a>
              </div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Regulatory Body:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedInstitution?.nita_number || 'N/A' }}</div>
            </div>
            <div>
              <label class="col-span-4 text-gray-600 text-sm mb-1">Reg Document:</label>
              <div class="mt-2">
                <div class="border border-gray-200 rounded p-4 bg-gray-50">
                  <div class="flex items-center justify-between bg-white border border-gray-200 rounded p-2">
                    <div class="flex items-center">
                      <div class="bg-red-100 p-1 rounded mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500" fill="none"
                          viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span class="text-sm">{{ selectedInstitution?.institution_document ? 'Reg Document' : 'No Document' }}</span>
                    </div>
                    <a v-if="selectedInstitution?.institution_document" :href="selectedInstitution.institution_document"
                      target="_blank" class="text-gray-500 p-1">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <h2 class="text-xl font-bold mb-4">Admin Information</h2>
          <div class="bg-white rounded-md border border-gray-200 p-5 mb-6">
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Name:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedInstitution?.institution_admins[0]?.admin_name || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Email Address:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedInstitution?.institution_admins[0]?.admin_email || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Phone Number:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedInstitution?.institution_admins[0]?.admin_phone || 'N/A' }}</div>
            </div>
          </div>


          <h2 class="text-xl font-bold mb-4">Onboarding Payment</h2>
          <div class="bg-white rounded-md border border-gray-200 p-5 mb-6">
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Payment Status:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">
                <span :class="{
                  'px-4 py-1 rounded-md text-text-lg': true,
                   'bg-red-200 text-red-800': selectedInstitution.payment_status === false,
                  'bg-green-200 text-green-800': selectedInstitution.payment_status === true,
                }">
                  {{ selectedInstitution.payment_status === false ? 'Pending' : 'Paid' }}
                </span>
              </div>
            </div>
            <div v-if="selectedInstitution.payment_status === false"  class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Prompt :</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">
                <button @click="remindPayment" class="bg-orange-400 text-white px-4 py-2 rounded hover:bg-orange-600">{{ remindingPayment ? 'Sending...' : 'Send Reminder'}}</button>
              </div>
            </div>
            <div v-if="selectedInstitution.payment_status === false" class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Last Reminded At:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ formattedDate(selectedInstitution?.reminded_at) || 'N/A' }}</div>
            </div>
          </div>

          <!-- Suspension/Rejection Reason Section -->
          <div v-if="['Suspended', 'Verification Rejected', 'Activation Rejected'].includes(selectedInstitution?.status.name)">
            <h2 class="text-xl font-bold mb-4">
              {{ selectedInstitution?.status.name === 'Suspended' ? 'Suspension reason' : 
                selectedInstitution?.status.name === 'Verification Rejected' ? 'Verification rejection reason' : 
                'Activation rejection reason' }}
            </h2>
            <div class="bg-white rounded-md border border-gray-200 p-0 mb-6">
              <textarea
                :value="getLatestReason()"
                class="w-full p-4 bg-gray-50 text-gray-700 border-0 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
                rows="5"
                placeholder="No reason provided"
                readonly></textarea>
            </div>
          </div>

          <h2 class="text-xl font-bold mb-4">Notes</h2>
          <div class="bg-white rounded-md border border-gray-200 p-0 mb-6">
            <textarea v-model="notes"
              class="w-full p-4 bg-gray-50 text-gray-700 border-0 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
              rows="5" placeholder="Add comments..."></textarea>
          </div>
        </div>
      </div>
      <!-- Action Buttons (unchanged) -->
      <div class="p-4 bg-gray-50 border-t border-gray-200">
        <div class="flex space-x-4">
          <button v-if="selectedInstitution?.status.name === 'Verified' || selectedInstitution?.status.name === 'Activation Rejected'" @click="activateInstitution"
            class="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded font-medium transition-colors"
            :disabled="loading">
            Activate
          </button>
          <button v-if="selectedInstitution?.status.name === 'Suspended'" @click="reActivateInstitution"
            class="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded font-medium transition-colors"
            :disabled="loading">
            Reactivate
          </button>
          <button v-if="selectedInstitution?.status.name === 'Pending' || selectedInstitution?.status.name === 'Rejected' || selectedInstitution?.status.name === 'Verification Rejected'"
            @click="verifyInstitution"
            class="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded font-medium transition-colors"
            :disabled="loading">
            Verify
          </button>
          <button v-if="selectedInstitution?.status.name === 'Pending'"
            @click="openVerificationRejectionModalForm"
            class="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded font-medium transition-colors"
            :disabled="loading">
            Reject
          </button>
          <button v-if="selectedInstitution?.status.name === 'Verified'"
            @click="openActivationRejectionModalForm"
            class="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded font-medium transition-colors"
            :disabled="loading">
            Reject
          </button>
          <button v-if="selectedInstitution?.status.name === 'Activated'" @click="openSuspensionModalForm"
            class="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded font-medium transition-colors"
            :disabled="loading">
            Suspend
          </button>
        </div>
      </div>
      <div class="h-1 bg-gray-100 mt-auto"></div>
    </div>

    <!-- Activation Rejection Modal -->
    <div v-if="showActivationRejectionModal"
      class="fixed inset-0 bg-gray-800 bg-opacity-70 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div class="bg-white rounded-md shadow-xl w-full max-w-2xl">
        <div class="flex justify-between items-center p-4 border-b">
          <h2 class="text-xl font-semibold px-2">Rejection Reason</h2>
          <button @click="closeRejectionModalForm" class="text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="p-6">
          <h2 class="text-md mb-4">Please provide a reason for rejecting the institution</h2>
          <div class="bg-white rounded-md border border-gray-200 p-0 mb-6">
            <textarea v-model="rejectionReason"
              class="w-full p-4 bg-gray-50 text-gray-700 border-0 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
              rows="8" placeholder="Add comments..."></textarea>
          </div>
        </div>
        <div class="bg-gray-50 px-6 py-4 flex justify-end border-t">
          <div class="flex space-x-3">
            <button @click="rejectActivatedInstitution"
              class="px-6 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-md transition-colors"
              :disabled="loading || !rejectionReason.trim()">
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Verification Rejection Modal -->
    <div v-if="showVerificationRejectionModal"
      class="fixed inset-0 bg-gray-800 bg-opacity-70 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div class="bg-white rounded-md shadow-xl w-full max-w-2xl">
        <div class="flex justify-between items-center p-4 border-b">
          <h2 class="text-xl font-semibold px-2">Rejection Reason</h2>
          <button @click="closeRejectionModalForm" class="text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="p-6">
          <h2 class="text-md mb-4">Please provide a reason for rejecting the institution</h2>
          <div class="bg-white rounded-md border border-gray-200 p-0 mb-6">
            <textarea v-model="rejectionReason"
              class="w-full p-4 bg-gray-50 text-gray-700 border-0 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
              rows="8" placeholder="Add comments..."></textarea>
          </div>
        </div>
        <div class="bg-gray-50 px-6 py-4 flex justify-end border-t">
          <div class="flex space-x-3">
            <button @click="rejectVerifiedInstitution"
              class="px-6 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-md transition-colors"
              :disabled="loading || !rejectionReason.trim()">
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Suspension Modal -->
    <div v-if="showSuspensionModal"
      class="fixed inset-0 bg-gray-800 bg-opacity-70 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div class="bg-white rounded-md shadow-xl w-full max-w-2xl">
        <div class="flex justify-between items-center p-4 border-b">
          <h2 class="text-xl font-semibold px-2">Suspension Reason</h2>
          <button @click="closeSuspensionModalForm" class="text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="p-6">
          <h2 class="text-md mb-4">Please provide a reason for suspending the institution</h2>
          <div class="bg-white rounded-md border border-gray-200 p-0 mb-6">
            <textarea v-model="suspensionReason"
              class="w-full p-4 bg-gray-50 text-gray-700 border-0 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
              rows="8" placeholder="Add comments..."></textarea>
          </div>
        </div>
        <div class="bg-gray-50 px-6 py-4 flex justify-end border-t">
          <div class="flex space-x-3">
            <button @click="suspendInstitution"
              class="px-6 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-md transition-colors"
              :disabled="loading || !suspensionReason.trim()">
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { InstitutionsAPI } from '@/services';
import { useToast } from 'vue-toastification';
import { setApiSystem } from '@/services/api';
import { useStore } from 'vuex';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

export default {
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  setup() {
    const toast = useToast();
    const store = useStore();
    const isAuthenticated = computed(() => store.getters.isAuthenticated);
    const router = useRouter();

    return { toast, store, isAuthenticated, router };
  },
  data() {
    return {
      institutions: [],
      statuses: [],
      currentPage: 1,
      totalPages: 1,
      showPanel: false,
      selectedInstitution: null,
      showActivationRejectionModal: false,
      showVerificationRejectionModal: false,
      showSuspensionModal: false,
      loading: false,
      notes: '',
      rejectionReason: '',
      suspensionReason: '',
      perPage: 10,
      totalItems: 0,
      remindingPayment: false
    };
  },
  created() {
    if (!this.isAuthenticated) {
      this.toast.error("Please log in to the eLearning system.");
      this.router.push('/login?system=eLearning');
      return;
    }
    setApiSystem('eLearning');
    this.fetchInstitutions();
  },
  watch: {
    searchQuery(newQuery) {
      this.fetchInstitutions(newQuery);
    },
  },
  methods: {
    async fetchInstitutions(searchQuery = this.searchQuery, page = this.currentPage, perPage = this.perPage) {
      try {
        this.loading = true;
        const params = { search: searchQuery || undefined, page, per_page: perPage };
        const response = await InstitutionsAPI.getAll(params);
        this.institutions = response.data.institutions || response.institutions;

        if (searchQuery && !response.data.filtered) {
          this.institutions = this.institutions.filter((inst) =>
            inst.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (inst.institution_admins[0]?.admin_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (inst.onboardedBy || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            inst.status.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }

        const stats = response.data.statistics || response.statistics;
        const meta = response.data.meta || response.meta;
        if (stats) this.$emit("update-statistics", stats);
        if (meta) {
          this.totalPages = meta.total_pages;
          this.currentPage = meta.current_page;
          this.perPage = meta.per_page;
          this.totalItems = meta.total_items;
        }
      } catch (error) {
        console.error('Fetch institutions error:', error.response?.data || error.message);
        this.toast.error(`Failed to fetch institutions: ${error.response?.data?.message || error.message}`);
        this.institutions = [];
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.fetchInstitutions(this.searchQuery, this.currentPage, this.perPage);
      }
    },
    getLatestReason() {
      if (!this.selectedInstitution || !this.selectedInstitution.institution_activation_logs?.length) return '';
      const currentStatusId = this.selectedInstitution.status.status_id;
      const logs = this.selectedInstitution.institution_activation_logs
        .filter(log => log.status_id === currentStatusId)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return logs.length > 0 ? logs[0].reason : '';
    },
    openPanel(institution) {
      this.selectedInstitution = institution;
      this.showPanel = true;
      this.notes = '';
    },
    closePanel() {
      this.showPanel = false;
      this.selectedInstitution = null;
      this.notes = '';
    },
    async verifyInstitution() {
      if (!this.selectedInstitution) return;
      this.loading = true;
      try {
        const payload = { reason: this.notes || 'Verification completed successfully' };
        await InstitutionsAPI.verifyInstitution(this.selectedInstitution.institution_id, payload);
        await this.fetchInstitutions();
        this.closePanel();
        this.toast.success("Institution verified successfully.");
      } catch (error) {
        this.toast.error(`Failed to verify institution: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    async activateInstitution() {
      if (!this.selectedInstitution) return;
      this.loading = true;
      try {
        const payload = { reason: this.notes || 'Institution activated successfully' };
        await InstitutionsAPI.activateInstitution(this.selectedInstitution.institution_id, payload);
        await this.fetchInstitutions();
        this.closePanel();
        this.toast.success("Institution activated successfully.");
      } catch (error) {
        this.toast.error(`Failed to activate institution: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    async reActivateInstitution() {
      if (!this.selectedInstitution) return;
      this.loading = true;
      try {
        const payload = { reason: this.notes || 'Institution reactivated successfully' };
        await InstitutionsAPI.reActivateInstitution(this.selectedInstitution.institution_id, payload);
        await this.fetchInstitutions();
        this.closePanel();
        this.toast.success("Institution reactivated successfully.");
      } catch (error) {
        this.toast.error(`Failed to reactivate institution: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    async rejectActivatedInstitution() {
      if (!this.selectedInstitution || !this.rejectionReason.trim()) {
        this.toast.warning("Please provide a rejection reason.");
        return;
      }
      this.loading = true;
      try {
        const payload = { reason: this.rejectionReason };
        await InstitutionsAPI.rejectActivation(this.selectedInstitution.institution_id, payload);
        await this.fetchInstitutions();
        this.closeActivationRejectionModalForm();
        this.closePanel();
        this.toast.success("Institution activation rejected successfully");
      } catch (error) {
        this.toast.error(`Failed to reject institution activation: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    async rejectVerifiedInstitution() {
      if (!this.selectedInstitution || !this.rejectionReason.trim()) {
        this.toast.warning("Please provide a rejection reason.");
        return;
      }
      this.loading = true;
      try {
        const payload = { reason: this.rejectionReason };
        await InstitutionsAPI.rejectVerification(this.selectedInstitution.institution_id, payload);
        await this.fetchInstitutions();
        this.closeVerificationRejectionModalForm();
        this.closePanel();
        this.toast.success("Institution verification rejected successfully");
      } catch (error) {
        this.toast.error(`Failed to reject institution verification: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    async suspendInstitution() {
      if (!this.selectedInstitution || !this.suspensionReason.trim()) {
        this.toast.warning("Please provide a suspension reason.");
        return;
      }
      this.loading = true;
      try {
        const payload = { reason: this.suspensionReason };
        await InstitutionsAPI.suspendInstitution(this.selectedInstitution.institution_id, payload);
        await this.fetchInstitutions();
        this.closeSuspensionModalForm();
        this.closePanel();
        this.toast.success("Institution suspended successfully");
      } catch (error) {
        this.toast.error(`Failed to suspend institution: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    async remindPayment() {
      try {
        this.remindingPayment = true;
        await InstitutionsAPI.remindPayment(this.selectedInstitution.institution_id);
        this.toast.success("Onboarding fee payment reminder sent successfully.");
      } catch (error) {
        this.toast.error(`Failed to send onboarding fee payment reminder: ${error.message}`);
      } finally {
        this.remindingPayment = false;
      }
    },
    openVerificationRejectionModalForm() {
      this.showVerificationRejectionModal = true;
      this.rejectionReason = '';
    },
    closeVerificationRejectionModalForm() {
      this.showVerificationRejectionModal = false;
      this.rejectionReason = '';
    },
    openActivationRejectionModalForm() {
      this.showActivationRejectionModal = true;
      this.rejectionReason = '';
    },
    closeActivationRejectionModalForm() {
      this.showActivationRejectionModal = false;
      this.rejectionReason = '';
    },
    openSuspensionModalForm() {
      this.showSuspensionModal = true;
      this.suspensionReason = '';
    },
    closeSuspensionModalForm() {
      this.showSuspensionModal = false;
      this.suspensionReason = '';
    },
    normalizedUrl(url) {
      if (!url) return '#';
      return url.match(/^https?:\/\//) ? url : `https://${url}`;
    },
    formatUrl(url) {
      if (!url) return 'N/A';
      return url.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    },
    formattedDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString();
    }
  },
};
</script>

<style>
.flex { display: flex; }
.transition-all { transition: width 0.3s ease-in-out; }
.bg-gray-50 { background-color: #f9fafb; }
.overflow-x-auto { overflow-x: auto; }
</style>